import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { getNavGroups, type NavItem } from './navItems';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'sidebar_collapsed';

function getStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function setStoredCollapsed(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function isActive(item: NavItem, pathname: string): boolean {
  const target = item.to.split('?')[0]!;
  if (item.matchPrefix) {
    return pathname === target || pathname.startsWith(`${target}/`);
  }
  return pathname === target;
}

export function Sidebar() {
  const { can, isAdmin, isSuperAdmin, isFPAAdmin } = useAuth();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(getStoredCollapsed);

  useEffect(() => {
    setStoredCollapsed(collapsed);
  }, [collapsed]);

  const groups = getNavGroups({
    can,
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    isFPAAdmin: isFPAAdmin(),
  });

  return (
    <aside
      className={cn(
        'hidden border-r border-border bg-card md:flex md:flex-col',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className="flex-1 overflow-y-auto py-4">
        {groups.map((group, gi) => (
          <div key={group.id} className="mb-4">
            {!collapsed && (
              <div className="px-4 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
            )}
            {collapsed && gi > 0 && <Separator className="my-2" />}
            <ul className="space-y-0.5 px-2">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item, pathname);
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-2 py-2 text-sm transition',
                        active
                          ? 'bg-primary/10 font-medium text-primary'
                          : 'text-foreground/80 hover:bg-muted',
                        collapsed && 'justify-center',
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-full', collapsed && 'px-0')}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {!collapsed && <span className="ml-2">Collapse</span>}
        </Button>
      </div>
    </aside>
  );
}

export default Sidebar;
